import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { PAGE_SIZES } from "../carbon/DataTable/DataTable";
import EmptyState from "../carbon/EmptyState/EmptyState";
import DeliveriesFileUploadModal from "./DeliveriesFileUploadModal";
import { buildDeliveryRows, deliveryListHeaders } from "./deliveryListRows";
import { DeliveryUpdateRequest, PaginatedDeliveryInterface } from "../../types";
import "./deliveriesTable.scss";

export interface DeliveriesTableProps {
  deliveries: PaginatedDeliveryInterface | undefined;
  pageNumber: number;
  setPageNumber: (page: number) => void;
  /** Controlled items-per-page — the `DeliveryController` list endpoints now honour a `size`
   * param, so both delivery lists (buyer + seller) drive it from their own state. */
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  /** Buyer-only. Grepped consumers (Task 4): `Deliveries.tsx` (buyer) passes this — it renders
   * the schedule action on a DRAFT row and drives `useUpdateDelivery`. `SupplierDeliveries.tsx`
   * (seller) omits it entirely, same as the pre-Carbon component's own `onUpdate?` contract — the
   * seller table is otherwise byte-identical (same headers, same PO-file upload action, same
   * `state` pill), so this one shared component covers both rather than forking one per role. */
  updateDelivery?: ({ deliveryId, endpoint }: DeliveryUpdateRequest) => void;
  /** Whether the PO-file upload action is available. Buyer table: true (default). Seller table
   * (`SupplierDeliveries`): false — `PUT /deliveries/{id}/purchase-order-file` is buyer-company
   * scoped (Deliveries flow T2), so a seller upload would only 403; an un-filed row shows a neutral
   * placeholder instead of a dead button, and the upload modal is not mounted. */
  canUploadFile?: boolean;
  /** No single message fits both "this company has no draft deliveries" and "no deliveries at
   * all" (the supplier table) — same reasoning `DataTable`'s own `empty` doc gives, just resolved
   * one level up here since only the caller (buyer tab state vs. supplier's fixed message) knows
   * which applies. */
  emptyTitle: string;
}

/**
 * Shared Carbon `DataTable` instance for both delivery lists (Task 4). Replaces the pre-Carbon
 * plain `<table>` (`DeliveriesTable.tsx`) + `DeliveriesTableRow.tsx` (which also owned the
 * PO-file upload `Dialog`, now split out to `DeliveriesFileUploadModal.tsx`) and their Tailwind
 * markup wholesale.
 *
 * Rows are read straight off `deliveries.content` — an already server-paged, server-filtered
 * page (`GET /deliveries/state/{state}/{page}` for the buyer table, `GET /deliveries/user/{page}`
 * for the supplier one) — never re-filtered client-side, same law every other migrated list in
 * this app follows.
 */
const DeliveriesTable = ({
  deliveries,
  pageNumber,
  setPageNumber,
  pageSize,
  setPageSize,
  isLoading,
  updateDelivery,
  canUploadFile = true,
  emptyTitle,
}: DeliveriesTableProps) => {
  const { t } = useTranslation();
  const [uploadDeliveryId, setUploadDeliveryId] = useState<string | null>(null);

  const headers = useMemo(() => deliveryListHeaders(t), [t]);

  const rows = useMemo(
    () =>
      buildDeliveryRows(deliveries?.content, t, {
        onUploadClick: canUploadFile ? setUploadDeliveryId : undefined,
        onScheduleClick: updateDelivery
          ? (deliveryId) => updateDelivery({ deliveryId, endpoint: "schedule" })
          : undefined,
      }),
    [deliveries, t, updateDelivery, canUploadFile]
  );

  const empty = <EmptyState title={emptyTitle} />;

  return (
    <>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        pagination={{
          // DataTable's own Pagination (Carbon) is 1-indexed; this screen's paging state — and
          // the `useDeliveries`/`useDeliveriesBySupplier` query it drives — stays 0-indexed to
          // match the API, same boundary conversion Orders.tsx/Suppliers.tsx use.
          page: pageNumber + 1,
          pageSize,
          pageSizes: PAGE_SIZES,
          totalItems: deliveries?.totalElements ?? 0,
          onPageChange: (newPage, newPageSize) => {
            setPageNumber(newPage - 1);
            setPageSize(newPageSize);
          },
        }}
        empty={empty}
      />
      {canUploadFile && (
        <DeliveriesFileUploadModal
          deliveryId={uploadDeliveryId}
          onClose={() => setUploadDeliveryId(null)}
        />
      )}
    </>
  );
};

export default DeliveriesTable;
